import type { IntlShape } from 'react-intl-next';

import type { DocNode } from '@atlaskit/adf-schema';
import { type ViewMode } from '@atlaskit/editor-plugin-editor-viewmode';
import type { PlaceholderPluginOptions } from '@atlaskit/editor-plugin-placeholder';
import { fg } from '@atlaskit/platform-feature-flags';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';

import { i18n } from './i18n';

interface Props {
	intl: IntlShape;
	options: {
		isAIEnabled: boolean;
		viewMode: ViewMode | undefined;
	};
}

/**
 * Helper to create a text node in ADF with optional marks
 * @param text - The text content
 * @param marks - Optional array of marks to apply (e.g., [{ type: 'code' }], [{ type: 'strong' }])
 * @returns A text node compatible with ADF inline content
 */
const createTextNode = (text: string, marks?: Array<Record<string, unknown>>) => ({
	type: 'text' as const,
	text,
	...(marks && marks.length > 0 && { marks }),
});

/**
 * Helper to create a space text node
 * Note: Spaces must be separate text nodes to prevent HTML whitespace collapsing around shortcuts with marks
 */
const createSpaceNode = () => createTextNode(' ');

const CODE_MARK = [{ type: 'code' as const }];

const createSpaceShortcutPlaceholderADF = (intl: IntlShape): DocNode =>
	({
		version: 1,
		type: 'doc',
		content: [
			{
				type: 'paragraph',
				content: [
					createTextNode(
						intl.formatMessage(i18n.editorEmptyDocumentSpaceShortcutPlaceholderADFPrefix),
					),
					createSpaceNode(),
					createTextNode(
						intl.formatMessage(i18n.editorEmptyDocumentSpaceShortcutPlaceholderADFSpaceShortcut),
						CODE_MARK,
					),
					createSpaceNode(),
					createTextNode(
						intl.formatMessage(i18n.editorEmptyDocumentSpaceShortcutPlaceholderADFMiddle),
					),
					createSpaceNode(),
					createTextNode(
						intl.formatMessage(i18n.editorEmptyDocumentSpaceShortcutPlaceholderADFSlashShortcut),
						CODE_MARK,
					),
					createSpaceNode(),
					createTextNode(
						intl.formatMessage(i18n.editorEmptyDocumentSpaceShortcutPlaceholderADFSuffix),
					),
				],
			},
		],
	}) as DocNode;

export function placeholderPluginOptions({ intl, options }: Props): PlaceholderPluginOptions {
	const shouldShowSpaceShortcut =
		(options.isAIEnabled && fg('platform_editor_ai_aifc_patch_beta_2')) ||
		(!options.isAIEnabled && fg('platform_editor_ai_aifc_patch_beta'));

	const placeholder = (() => {
		// SECTION: From confluence/next/packages/full-page-editor/src/FullPageEditorComponent.tsx `const placeholderText = `
		if (options.viewMode === 'view') {
			return undefined;
		}
		// END SECTION

		// SECTION: From confluence/next/packages/full-page-editor/src/FullPageEditorComponent.tsx `_getPlaceholderText()`
		if (shouldShowSpaceShortcut && !fg('platform_editor_ai_aifc_patch_ga')) {
			return intl.formatMessage(i18n.editorEmptyDocumentSpaceShortcutPlaceholder);
		}

		if (!options.isAIEnabled && editorExperiment('platform_editor_ai_quickstart_command', true)) {
			return intl.formatMessage(i18n.editorEmptyDocumentPlaceholderAI);
		}

		if (editorExperiment('platform_editor_controls', 'variant1')) {
			return intl.formatMessage(i18n.defaultPlaceholder);
		}

		if (!options.isAIEnabled) {
			return intl.formatMessage(i18n.aiPlaceholder);
		}

		return intl.formatMessage(i18n.easyMentionsPlaceholder);
		// END SECTION
	})();

	const placeholderADF: DocNode | undefined = (() => {
		if (shouldShowSpaceShortcut && fg('platform_editor_ai_aifc_patch_ga')) {
			return createSpaceShortcutPlaceholderADF(intl);
		}
		return undefined;
	})();

	return {
		placeholder,
		placeholderADF,
		withEmptyParagraph: fg('platform_editor_ai_aifc_patch_beta_2'),
	};
}
